import { useEffect, useMemo, useState } from "react";
import { useHeroSlides } from "@/hooks/useHeroSlides";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const { slides } = useHeroSlides();
  const { settings } = useSiteSettings();

  useEffect(() => {
    if (slides.length === 0) return;
    setActiveIndex((current) => (current >= slides.length ? 0 : current));
  }, [slides]);

  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, 6000);

    return () => window.clearInterval(interval);
  }, [slides.length]);

  const activeSlide = useMemo(() => slides[activeIndex] ?? slides[0], [activeIndex, slides]);

  const goToPrevious = () => {
    setActiveIndex((current) => (current - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setActiveIndex((current) => (current + 1) % slides.length);
  };

  if (!activeSlide) {
    return null;
  }

  return (
    <section className="relative flex min-h-[248px] items-center overflow-hidden md:min-h-[288px]">
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === activeIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.imageUrl}
              alt={slide.categoryLabel}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/35"></div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={goToPrevious}
            aria-label="Slide anterior"
            className="absolute left-3 md:left-5 top-1/2 z-20 -translate-y-1/2 w-11 h-11 rounded-full border border-orange-200/40 bg-orange-400/30 text-white hover:bg-orange-400/45 transition-colors backdrop-blur-sm cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={goToNext}
            aria-label="Próximo slide"
            className="absolute right-3 md:right-5 top-1/2 z-20 -translate-y-1/2 w-11 h-11 rounded-full border border-orange-200/40 bg-orange-400/30 text-white hover:bg-orange-400/45 transition-colors backdrop-blur-sm cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </>
      )}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-6 md:pt-12 md:pb-8">
        <div className="max-w-4xl">
          {settings.heroTitle.trim() && (
            <h1 className="text-[1.05rem] md:text-[1.75rem] font-normal text-white leading-tight mb-3 whitespace-pre-line">
              {settings.heroTitle}
            </h1>
          )}
          {settings.heroSubtitle.trim() && (
            <p className="text-white/75 text-sm md:text-base max-w-3xl whitespace-pre-line">
              {settings.heroSubtitle}
            </p>
          )}
        </div>
      </div>

      <div className="absolute bottom-2 right-4 md:bottom-2 md:right-6 z-20">
        <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-300/20 px-3 py-1 opacity-100">
          <i className="ri-fire-line text-orange-300 text-xs"></i>
          <span className="text-orange-200 text-xs font-medium">+10k produtos analisados</span>
        </div>
      </div>

      {slides.length > 1 && (
        <div className="absolute left-1/2 bottom-2 z-20 flex -translate-x-1/2 items-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`Ir para slide ${index + 1}`}
              className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 cursor-pointer ${
                index === activeIndex ? "bg-orange-400" : "bg-white/45 hover:bg-white/70"
              }`}
            ></button>
          ))}
        </div>
      )}

    </section>
  );
}